import { useEffect, useRef, useState } from "react";
import { Box } from "components";
import useIOSDetection from "hooks/useIOSDetection";
import useScanAnalytics from "hooks/useScanAnalytics";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import { useLocation } from "react-router-dom";
import { useValidatePassportMutation } from "redux/queries/passport.query";
import { useGetSettingsQuery } from "redux/queries/settings.query";
import { classifyError, ERROR_CATEGORIES } from "utils/errorClassifier";
import { parseTerminalId } from "utils/parseTerminalId";

import LanguageDrawer from "./flightList/LanguageDrawer";
import Body from "./scanPassport/Body";
import CircleStyled from "./scanPassport/CircleStyled";
import Header from "./scanPassport/Header";
import ImagePreviewModal from "./scanPassport/ImagePreviewModal";
import NotEligibleContent from "./scanPassport/NotEligibleContent";
import OCRScanner from "./scanPassport/OCRScanner";
import PreCameraDrawer from "./scanPassport/PreCameraDrawer";
import STEPS from "./scanPassport/Steps";
import TryAgainContent from "./scanPassport/TryAgainContent";
import ValidationDrawer from "./scanPassport/ValidationDrawer";

export default function ScanPassport() {
  const { sendAnalytics } = useTrackPageAnalytics();
  const { data: settingsData = {} } = useGetSettingsQuery(undefined, {
    refetchOnReconnect: true,
  });

  const { featureMap: featureFlags } = settingsData;
  const areAnalyticsEnabled = !featureFlags?.bypass_passport_scanning_analytics;
  const scanAnalytics = useScanAnalytics(areAnalyticsEnabled);
  const iosDetection = useIOSDetection();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const source = params.get("source");

  const terminal = parseTerminalId(params.get("terminal"));
  const area = params.get("area");
  const subArea = params.get("subArea");
  const isPreviewEnabled = params.get("preview") === "true"; // Enable image preview with ?preview=true

  // location QR → terminal/area/sub-area touch point; terminal → terminal QR;
  // source only → generic QR; neither → direct click
  const analyticsKey = (() => {
    if (area || subArea) {
      return {
        key: "navigate-smart-gate-location-qr",
        name: "Accessed Smart Gate from Location QR",
      };
    }
    if (terminal) {
      return {
        key: "navigate-smart-gate-terminal-qr",
        name: "Accessed Smart Gate from Terminal QR",
      };
    }
    if (source === "qr" || source === "smart-gate") {
      return {
        key: "navigate-smart-gate-qr",
        name: "Accessed Smart Gate from Generic QR",
      };
    }
    return {
      key: "navigate-smart-gate-click",
      name: "Accessed Smart Gate from Main Menu click",
    };
  })();

  useTrackPageAnalytics({ ...analyticsKey, terminal, area, sub_area: subArea });

  useEffect(() => {
    scanAnalytics.markFeatureEntry();
    scanAnalytics.setSource(source, terminal, area, subArea);
    const ua = navigator.userAgent;
    const androidVersion = ua.match(/Android\s+([\d.]+)/i)?.[1];

    const isIos =
      /iP(ad|hone|od)/i.test(ua) ||
      (iosDetection.isMacOS && navigator.maxTouchPoints > 1);
    let osString = "unknown";
    if (isIos) osString = "ios_mobile_safari";
    else if (/Android/i.test(ua)) osString = "android";
    else if (iosDetection.isMacOS) osString = "macos";
    else if (/Windows/i.test(ua)) osString = "windows";
    const version = androidVersion || iosDetection.iOSVersion;
    scanAnalytics.setOS(osString + (version ? `_${version}` : ""));
  }, [scanAnalytics, iosDetection, source, terminal, area, subArea]);

  // Safety net for sessions that never receive a validation response: tab
  // close, iOS swipe-kill, app backgrounding, or kiosk WebView teardown.
  // submit() is idempotent — if the response already submitted, these no-op.
  const scanAnalyticsRef = useRef(scanAnalytics);
  scanAnalyticsRef.current = scanAnalytics;
  useEffect(() => {
    const flush = (detail) =>
      scanAnalyticsRef.current.submit("browser_exit", detail);
    const handlePageHide = () => flush("page_hidden");
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush("visibility_hidden");
    };
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validatePassport, { isLoading: isValidating }] =
    useValidatePassportMutation();
  const [scannerKey, setScannerKey] = useState(0);
  const [passportData, setPassportData] = useState(null);
  const [showPreCameraDrawer, setShowPreCameraDrawer] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [capturedImages, setCapturedImages] = useState(null);

  // Tracks how many consecutive technical errors have occurred to enforce the max-1-retry policy
  const [technicalRetryCount, setTechnicalRetryCount] = useState(0);
  // Controls visibility of the Not Eligible screen (shown for document info errors or after max retries)
  const [showNotEligible, setShowNotEligible] = useState(false);
  // Controls visibility of the standalone "Try Again" page for first technical errors
  const [showTryAgain, setShowTryAgain] = useState(false);

  const pauseScanner = useRef(false);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      return;
    }
    if (areAnalyticsEnabled) {
      setShowPreCameraDrawer(true);
    } else {
      setShowScanner(true);
    }
  };

  const handleSkip = () => {
    if (areAnalyticsEnabled) {
      setShowPreCameraDrawer(true);
    } else {
      setShowScanner(true);
    }
  };

  const handleScanComplete = async (imageData) => {
    if (pauseScanner.current) {
      return;
    }
    pauseScanner.current = true;

    const { originalImage, processedImage } = imageData;

    // Log image info
    console.log(
      "📸 Original image:",
      (originalImage.size / 1024).toFixed(2),
      "KB"
    );
    console.log(
      "📸 Processed image:",
      (processedImage.size / 1024).toFixed(2),
      "KB (cropped & grayscale)"
    );

    // Store images for preview or validation
    setCapturedImages({ originalImage, processedImage });

    sendAnalytics({
      key: "smart-gate-image-captured",
      type: "event",
      name: "Successfully captured passport image",
    });

    if (isPreviewEnabled) {
      // Preview mode: Show modal for image inspection
      setShowImagePreview(true);
    } else {
      // Production mode: Go straight to validation
      await validatePassportImage(processedImage);
    }
  };

  const validatePassportImage = async (processedImage) => {
    // Mark image submission timestamp
    scanAnalytics.markImageSubmitted();

    // Show validation drawer with loading state
    setIsDrawerOpen(true);

    try {
      // Create FormData to send processed image to backend
      const formData = new FormData();
      formData.append("passport_image", processedImage, "passport.jpg");

      const response = await validatePassport(formData).unwrap();

      // Reason: the RTK Query queryFn wraps errors inside { data: { status: "error" } }
      // so a "successful" unwrap can still contain an error status
      if (response?.status === "error") {
        scanAnalytics.markFlowEnd();
        handleValidationError(response);
        return;
      }

      // Extract backend timestamps from response
      if (response?.timestamps) {
        scanAnalytics.setBackendTimestamps(response.timestamps);
      }

      // Backend should return passport data along with validation result
      if (response?.data?.passportData) {
        const passportData = response.data.passportData;
        setPassportData(passportData);

        // Set nationality in analytics if available
        if (passportData.passportNationality) {
          scanAnalytics.setNationality(passportData.passportNationality);
        }

        // Set gender in analytics if available
        if (passportData.genderId) {
          scanAnalytics.setGender(passportData.genderId);
        }

        // Set date of birth in analytics if available
        if (passportData.dateOfBirth) {
          scanAnalytics.setDateOfBirth(passportData.dateOfBirth);
        }
      }

      scanAnalytics.markFlowEnd();
      scanAnalytics.markTotalTimeSpent();

      const isEligible = !!response?.data?.result;
      if (isEligible) {
        scanAnalytics.incrementEligible();
        scanAnalytics.submit("eligible", "passport_eligible");
      } else {
        scanAnalytics.incrementNonEligible();
        scanAnalytics.submit("not_eligible_result", "passport_not_eligible");
      }

      setTechnicalRetryCount(0);
      setValidationResult(response);
    } catch (error) {
      // Mark flow end even on error
      scanAnalytics.markFlowEnd();

      const errorResult = {
        status: "error",
        message:
          error?.data?.message ||
          error?.message ||
          "Unable to validate passport. Please try again.",
        statusCode: error?.status || error?.data?.statusCode,
      };

      handleValidationError(errorResult);
    }
  };

  /**
   * Classifies the error and decides the appropriate UI flow:
   * - DOCUMENT_INFO → Show Not Eligible immediately (no retry)
   * - TECHNICAL_ISSUE (first attempt) → Show standalone "Try Again" page
   * - TECHNICAL_ISSUE (after retry) → Show Not Eligible
   *
   * Also captures the full error details (http_status, fault_code, message, action)
   * in the scan analytics event for DB storage.
   */
  const handleValidationError = (errorResult) => {
    const { category, faultCode } = classifyError(errorResult);

    // Increment the error counter for every validation failure
    scanAnalytics.incrementError();

    sendAnalytics({
      key: "smart-gate-validation-error",
      type: "event",
      name: `Validation error: ${category}${
        faultCode ? ` (${faultCode})` : ""
      }`,
    });

    const errorSubdoc = {
      http_status: errorResult.statusCode || null,
      fault_code: faultCode || null,
      message: errorResult.message || null,
    };

    if (category === ERROR_CATEGORIES.DOCUMENT_INFO) {
      // Scenario B: Document info issue → show Not Eligible immediately
      scanAnalytics.setError({ ...errorSubdoc, action: "not_eligible" });
      scanAnalytics.submit(
        "document_info_error",
        "document_issue_not_eligible"
      );

      setIsDrawerOpen(false);
      setShowNotEligible(true);
      return;
    }

    // Scenario A: Technical issue
    if (technicalRetryCount >= 1) {
      // Already retried once → show Not Eligible
      scanAnalytics.setError({ ...errorSubdoc, action: "not_eligible" });
      scanAnalytics.submit(
        "error_threshold_reached",
        "technical_error_max_retries"
      );

      setIsDrawerOpen(false);
      setShowNotEligible(true);
      return;
    }

    // First technical error → show standalone "Try Again" page
    scanAnalytics.setError({ ...errorSubdoc, action: "retry_1" });
    scanAnalytics.submit("technical_error_retry_1", "first_technical_error");

    setTechnicalRetryCount((prev) => prev + 1);
    setIsDrawerOpen(false);
    setShowScanner(false);
    setShowTryAgain(true);
  };

  const handleContinueFromPreview = async () => {
    // Close preview modal
    setShowImagePreview(false);

    if (!capturedImages) return;

    const { processedImage } = capturedImages;
    await validatePassportImage(processedImage);
  };

  const handleClose = () => {
    // No-op if the validation response already submitted; otherwise this
    // captures pre-response abandonment.
    scanAnalytics.submit("user_exit", "user_exited_feature");
    sendAnalytics({
      key: "smart-gate-click-close",
      type: "event",
      name: "User clicked on 'X' button and was redirected to main page",
    });
    window.location.href = "/";
  };

  const handleSuccessExit = () => {
    sendAnalytics({
      key: "smart-gate-success-exit",
      type: "event",
      name: "User exited after successful passport scan",
    });
    window.location.href = "/";
  };

  const handleDrawerClose = () => {
    setValidationResult(null);
    setIsDrawerOpen(false);
    setShowScanner(true);
    setScannerKey((prev) => prev + 1);
    pauseScanner.current = false;
  };

  const handlePreCameraContinue = () => {
    setShowPreCameraDrawer(false);
    setShowScanner(true);
  };

  const handlePreCameraCancel = () => {
    scanAnalytics.submit("user_exit", "pre_camera_cancel");
    sendAnalytics({
      key: "smart-gate-pre-camera-cancel",
      type: "event",
      name: "User cancelled at pre-camera drawer",
    });
    window.location.href = "/";
  };

  const handleImagePreviewClose = () => {
    setShowImagePreview(false);
    setCapturedImages(null);
  };

  const handleTryAgain = () => {
    // Start a new session linked to the just-submitted technical_error_retry_1 row.
    scanAnalytics.chainNext();
    setShowTryAgain(false);
    setValidationResult(null);
    setShowScanner(true);
    setScannerKey((prev) => prev + 1);
    pauseScanner.current = false;
  };

  const handleNotEligibleExit = () => {
    // The scan_timing row was already submitted in handleValidationError.
    sendAnalytics({
      key: "smart-gate-not-eligible-exit",
      type: "event",
      name: "User exited after seeing Not Eligible screen",
    });
    window.location.href = "/";
  };

  const handleNotEligibleScanAnother = () => {
    // The failed-scan row was already submitted; start a new linked session.
    scanAnalytics.chainNext();
    sendAnalytics({
      key: "smart-gate-not-eligible-scan-another",
      type: "event",
      name: "User chose to scan another passport after Not Eligible",
    });

    setShowNotEligible(false);
    setTechnicalRetryCount(0);
    setValidationResult(null);
    setPassportData(null);
    setShowScanner(true);
    setScannerKey((prev) => prev + 1);
    pauseScanner.current = false;
  };

  if (showPreCameraDrawer) {
    if (!areAnalyticsEnabled) {
      setShowPreCameraDrawer(false);
      setShowScanner(true);
      return null;
    }
    return (
      <PreCameraDrawer
        isOpen={showPreCameraDrawer}
        onContinue={handlePreCameraContinue}
        onCancel={handlePreCameraCancel}
        message="Please get ready to scan your passport. Make sure your document is visible and well-lit."
      />
    );
  }

  if (showNotEligible) {
    return (
      <Box
        d="flex"
        direction="column"
        style={{
          height: "100dvh",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: "#fff",
        }}
      >
        <NotEligibleContent
          onContinue={handleNotEligibleExit}
          onScanAnother={handleNotEligibleScanAnother}
        />
      </Box>
    );
  }

  if (showTryAgain) {
    return (
      <TryAgainContent onTryAgain={handleTryAgain} onClose={handleClose} />
    );
  }

  if (showScanner) {
    return (
      <>
        <OCRScanner
          pauseScanner={pauseScanner}
          key={scannerKey}
          onScanComplete={handleScanComplete}
          onClose={handleClose}
          scanAnalytics={scanAnalytics}
        />
        <ValidationDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          validationResult={validationResult}
          isValidating={isValidating}
          onContinue={handleSuccessExit}
          scanAnalytics={scanAnalytics}
          nationality={passportData?.passportNationality}
          gender={passportData?.genderId}
          dateOfBirth={passportData?.dateOfBirth}
        />
        {showImagePreview && capturedImages && (
          <ImagePreviewModal
            originalImage={capturedImages.originalImage}
            processedImage={capturedImages.processedImage}
            onClose={handleImagePreviewClose}
            onContinue={handleContinueFromPreview}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Box
        style={{
          height: "100svh",
          backgroundColor: "#797FD6",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CircleStyled circlePosition={STEPS[currentStep].circlePosition} />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Header />
          <Body
            content={STEPS[currentStep]}
            currentStep={currentStep}
            numberOfSteps={STEPS.length}
            onNext={handleNext}
            onSkip={handleSkip}
          />
        </div>
        <LanguageDrawer />
      </Box>
    </>
  );
}
